import { PEOPLE_TEAM_ROUTES, SHARED_ROUTES } from "definitions/constants/routeConstants";
import { Email } from "features/Resources/Email/models";
import { EmailTemplate } from "features/Resources/EmailTemplate/models";
import { Message } from "features/Resources/Message/models";
import { MessageTemplate } from "features/Resources/MessageTemplate/models";
import { PrefixId, TemplateType } from "features/Resources/Shared/models";
import { TaskTemplate } from "features/Resources/TaskTemplate/models";
import { generatePath } from "helpers/url/pages/v1";
import { fromActionPrefixId } from "features/Resources/Shared/helpers";
import { Template, TemplateSummary } from "features/Resources/SharedTemplate/models";
import { Task } from "features/Resources/Task/models";
import { extractTemplate, fromTemplatePrefixId, isTemplateSummary } from "features/Resources/SharedTemplate/helpers";
import { Meeting } from "features/Resources/Meeting/models";
import { MeetingTemplate } from "features/Resources/MeetingTemplate/models";

export function emailPathById(emailId: string) {
  return generatePath({ path: SHARED_ROUTES.emails.show, params: { id: emailId || "" } });
}

export function emailPath(email: Email) {
  return emailPathById(email?.id);
}

export function emailTemplatePathById(emailTemplateid: string) {
  return generatePath({ path: PEOPLE_TEAM_ROUTES.emailTemplates.show, params: { id: emailTemplateid || "" } });
}

export function emailTemplatePath(emailTemplate: EmailTemplate) {
  return emailTemplatePathById(emailTemplate?.id);
}

export function meetingPathById(meetingId: string) {
  return generatePath({ path: SHARED_ROUTES.meetings.show, params: { id: meetingId || "" } });
}

export function meetingPath(meeting: Meeting) {
  return meetingPathById(meeting?.id);
}

export function meetingTemplatePathById(meetingTemplateid: string) {
  return generatePath({ path: PEOPLE_TEAM_ROUTES.meetingTemplates.show, params: { id: meetingTemplateid || "" } });
}

export function meetingTemplatePath(meetingTemplate: MeetingTemplate) {
  return meetingTemplatePathById(meetingTemplate?.id);
}

export function messagePathById(messageId: string) {
  return generatePath({ path: SHARED_ROUTES.messages.show, params: { id: messageId || "" } });
}

export function messagePath(message: Message) {
  return messagePathById(message?.id);
}

export function messageTemplatePathById(messageTemplateid: string) {
  return generatePath({ path: PEOPLE_TEAM_ROUTES.messageTemplates.show, params: { id: messageTemplateid || "" } });
}

export function messageTemplatePath(messageTemplate: MessageTemplate) {
  return messageTemplatePathById(messageTemplate?.id);
}

export function taskPathById(taskId: string) {
  return generatePath({ path: SHARED_ROUTES.tasks.show, params: { id: taskId || "" } });
}

export function taskPath(task: Task) {
  return taskPathById(task?.id);
}

export function taskTemplatePathById(taskTemplateid: string) {
  return generatePath({ path: PEOPLE_TEAM_ROUTES.taskTemplates.show, params: { id: taskTemplateid || "" } });
}

export function taskTemplatePath(taskTemplate: TaskTemplate) {
  return taskTemplatePathById(taskTemplate?.id);
}

export function checkpointPathById(checkpointId: string) {
  return generatePath({ path: SHARED_ROUTES.checkpoints.show, params: { id: checkpointId || "" } });
}

export function checkpointTemplatePathById(checkpointTemplateId: string) {
  return generatePath({
    path: PEOPLE_TEAM_ROUTES.checkpointTemplates.show,
    params: { id: checkpointTemplateId || "" },
  });
}

export function templatePathByTypeAndId(templateType: TemplateType, id: string): string {
  const templatePathMap = {
    checkpoint_template: (templateId: string) => checkpointTemplatePathById(templateId),
    email_template: (templateId: string) => emailTemplatePathById(templateId),
    meeting_template: (templateId: string) => meetingTemplatePathById(templateId),
    message_template: (templateId: string) => messageTemplatePathById(templateId),
    task_template: (templateId: string) => taskTemplatePathById(templateId),
  };

  const getPath = templatePathMap[templateType as keyof typeof templatePathMap];
  if (!getPath) {
    throw new Error(`Invalid template type: ${templateType}`);
  }

  return getPath(id);
}

export function templatePathByTemplateOrSummary(templateOrSummary: Template | TemplateSummary): string {
  const resolvedTemplate = extractTemplate(templateOrSummary);
  if (!resolvedTemplate) {
    return "";
  }

  const templateType = isTemplateSummary(templateOrSummary)
    ? templateOrSummary.templateType
    : fromTemplatePrefixId(resolvedTemplate.prefixId).templateType;

  return templatePathByTypeAndId(templateType as TemplateType, resolvedTemplate.id);
}

export function actionPathByPrefixId(prefixId: PrefixId | undefined) {
  if (!prefixId) {
    return "";
  }
  const { resourceType: actionType, id: actionId } = fromActionPrefixId(prefixId);

  const actionPathMap = {
    email: (id: string) => emailPathById(id),
    meeting: (id: string) => meetingPathById(id),
    message: (id: string) => messagePathById(id),
    task: (id: string) => taskPathById(id),
    checkpoint: (id: string) => checkpointPathById(id),
  };

  const getPath = actionPathMap[actionType as keyof typeof actionPathMap];
  if (!getPath) throw new Error(`Error calling actionPathByPrefixId - Invalid action type: ${actionType}`);

  return getPath(actionId as string);
}
